import type { ProjectsInProgressData } from './ProjectsInProgress.types'
import type { ProjectsPartnerOverviewData } from '~/store-v2/projects/useProjectsPartnerOverview'
import { useI18n } from 'vue-i18n'
import useAuth from '~/store-v2/auth/useAuth'
import useProjectsPartnerOverview from '~/store-v2/projects/useProjectsPartnerOverview'
import { useImpersonateStore } from '~/store/impersonate'
import date from '~/utils/date'
import { STEP_STATUS_MAPPING } from '~/utils/financing-flow/Financing'

export function useProjectsInProgress() {
  const router = useRouter()
  const { data: auth } = useAuth()
  const impersonateStore = useImpersonateStore()
  const { t } = useI18n()

  const basePartnerId = computed(() => {
    const perfil = auth.value?.me?.perfil
    if (perfil === 'admin' && impersonateStore.partnerId > 0) {
      return impersonateStore.partnerId.toString()
    }
    return auth.value?.person?.partner?.id?.toString() || ''
  })

  const {
    data: apiData,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useProjectsPartnerOverview(basePartnerId)

  const isProcessing = computed(() => {
    return apiData.value?.status === 'PROCESSING'
  })

  const hasNullData = computed(() => {
    if (!apiData.value || !isProcessing.value) return false
    return !apiData.value.all
      || !apiData.value.simulation
      || !apiData.value.proposal
      || !apiData.value.installation
  })

  const isEmpty = computed(() => {
    if (!apiData.value) return false
    return (apiData.value.all?.total ?? 0) === 0
  })

  const isRefreshing = computed(() => {
    return isLoading.value || isFetching.value || isProcessing.value
  })

  function formatLastUpdated(lastUpdatedAt: string | null | undefined): string {
    if (isError.value) return t('overview.projects_in_progress.last_updated.error')
    if (isRefreshing.value) return t('overview.projects_in_progress.last_updated.updating')
    if (!lastUpdatedAt) return t('overview.projects_in_progress.last_updated.now')

    const now = date()
    const updatedAt = date(lastUpdatedAt)
    const diffInMinutes = now.diff(updatedAt, 'minute')
    const diffInHours = now.diff(updatedAt, 'hour')
    const diffInDays = now.diff(updatedAt, 'day')

    if (diffInMinutes < 1) {
      return t('overview.projects_in_progress.last_updated.less_than_minute')
    }
    if (diffInMinutes < 60) {
      return t('overview.projects_in_progress.last_updated.minutes', diffInMinutes)
    }
    if (diffInHours < 24) {
      return t('overview.projects_in_progress.last_updated.hours', diffInHours)
    }
    return t('overview.projects_in_progress.last_updated.days', diffInDays)
  }

  function safeNumber(value: number | null | undefined): number {
    return value ?? 0
  }

  function transformApiDataToDisplayData(overviewData: ProjectsPartnerOverviewData): ProjectsInProgressData {
    const simulationTotal = safeNumber(overviewData.simulation?.approved)
      + safeNumber(overviewData.simulation?.reassassement)
    const proposalTotal = safeNumber(overviewData.proposal?.in_equipments)
      + safeNumber(overviewData.proposal?.in_customer_data)
      + safeNumber(overviewData.proposal?.in_biometry_and_contract)
      + safeNumber(overviewData.proposal?.in_payment_model)
    const installationTotal = safeNumber(overviewData.installation?.in_progress)
    const allTotal = safeNumber(overviewData.all?.in_progress)
      + safeNumber(overviewData.all?.in_reassassement)

    return {
      lastUpdated: formatLastUpdated(overviewData.last_updated_at),
      status: {
        total: allTotal,
        inProgress: safeNumber(overviewData.all?.in_progress),
        toRetrieve: safeNumber(overviewData.all?.in_reassassement),
      },
      categories: [
        {
          label: t('overview.projects_in_progress.categories.simulations'),
          total: simulationTotal,
          progress: 0,
          breakdowns: [
            { label: t('overview.projects_in_progress.breakdowns.approved_simulations'), count: safeNumber(overviewData.simulation?.approved) },
            { label: t('overview.projects_in_progress.breakdowns.reassessments'), count: safeNumber(overviewData.simulation?.reassassement), highlighted: true },
          ],
        },
        {
          label: t('overview.projects_in_progress.categories.proposals'),
          total: proposalTotal,
          progress: 0,
          breakdowns: [
            { label: t('overview.projects_in_progress.breakdowns.equipment'), count: safeNumber(overviewData.proposal?.in_equipments) },
            { label: t('overview.projects_in_progress.breakdowns.customer_data'), count: safeNumber(overviewData.proposal?.in_customer_data) },
            { label: t('overview.projects_in_progress.breakdowns.biometry_and_contract'), count: safeNumber(overviewData.proposal?.in_biometry_and_contract) },
            { label: t('overview.projects_in_progress.breakdowns.payment_model'), count: safeNumber(overviewData.proposal?.in_payment_model) },
          ],
        },
        {
          label: t('overview.projects_in_progress.categories.installations'),
          total: installationTotal,
          progress: 0,
          breakdowns: [
            { label: t('overview.projects_in_progress.breakdowns.installations_in_progress'), count: safeNumber(overviewData.installation?.in_progress) },
          ],
        },
      ],
    }
  }

  const displayData = computed(() => {
    if (isError.value) {
      return {
        lastUpdated: t('overview.projects_in_progress.last_updated.error'),
        status: {
          total: 0,
          inProgress: 0,
          toRetrieve: 0,
        },
        categories: [],
      } as ProjectsInProgressData
    }
    if (!apiData.value) {
      return {
        lastUpdated: t('overview.projects_in_progress.last_updated.now'),
        status: {
          total: 0,
          inProgress: 0,
          toRetrieve: 0,
        },
        categories: [],
      } as ProjectsInProgressData
    }
    return transformApiDataToDisplayData(apiData.value)
  })

  function progressBarWidth(value: number, total: number) {
    if (total === 0) return 0
    return (value / total) * 100
  }

  async function handleRefresh() {
    await refetch()
  }

  function handleViewAll() {
    router.push('/projects')
  }

  function handleTotalClick() {
    const allStatusSet = new Set<string>()
    Object.values(STEP_STATUS_MAPPING).forEach((statuses) => {
      statuses.forEach((status) => {
        if (status !== 'expired' && status !== 'canceled') {
          allStatusSet.add(status)
        }
      })
    })

    const allStatuses = Array.from(allStatusSet).sort()

    const query: Record<string, string> = {}
    if (allStatuses.length > 0) {
      query.status = allStatuses.join(',')
    }

    router.push({ path: '/projects', query })
  }

  function handleCategoryClick(category: string) {
    const categoryStepsMap: Record<string, string[]> = {
      [t('overview.projects_in_progress.categories.simulations')]: ['simulation'],
      [t('overview.projects_in_progress.categories.proposals')]: ['equipment', 'customer_data', 'biometry_and_contract', 'payment_model'],
      [t('overview.projects_in_progress.categories.installations')]: ['installation'],
    }

    const steps = categoryStepsMap[category] || []
    if (steps.length === 0) return

    const validStatusSet = new Set<string>()
    steps.forEach((step) => {
      const statuses = STEP_STATUS_MAPPING[step] || []
      const filteredStatuses = statuses.filter(status => status !== 'canceled' && status !== 'expired')
      filteredStatuses.forEach(status => validStatusSet.add(status))
    })

    const validStatuses = Array.from(validStatusSet).sort()

    const query: Record<string, string> = {
      steps: steps.join(','),
    }
    if (validStatuses.length > 0) {
      query.status = validStatuses.join(',')
    }

    router.push({ path: '/projects', query })
  }

  function handleBreakdownClick(category: string, breakdown: string) {
    const breakdownMap: Record<string, { step?: string, status?: string, provider?: string, useAllStatuses?: boolean }> = {
      [t('overview.projects_in_progress.breakdowns.approved_simulations')]: { step: 'simulation', status: 'approved' },
      [t('overview.projects_in_progress.breakdowns.reassessments')]: { provider: 'reassessment', useAllStatuses: true },
      [t('overview.projects_in_progress.breakdowns.equipment')]: { step: 'equipment' },
      [t('overview.projects_in_progress.breakdowns.customer_data')]: { step: 'customer_data' },
      [t('overview.projects_in_progress.breakdowns.biometry_and_contract')]: { step: 'biometry_and_contract' },
      [t('overview.projects_in_progress.breakdowns.payment_model')]: { step: 'payment_model' },
      [t('overview.projects_in_progress.breakdowns.installations_in_progress')]: { step: 'installation', status: 'in_progress' },
    }
    const filter = breakdownMap[breakdown]
    if (!filter) return

    const query: Record<string, string> = {}

    if (filter.useAllStatuses) {
      if (filter.provider) query.provider = filter.provider

      const allStatusSet = new Set<string>()
      Object.values(STEP_STATUS_MAPPING).forEach((statuses) => {
        statuses.forEach((status) => {
          if (status !== 'expired' && status !== 'canceled') {
            allStatusSet.add(status)
          }
        })
      })

      const allStatuses = Array.from(allStatusSet).sort()
      if (allStatuses.length > 0) {
        query.status = allStatuses.join(',')
      }
    }
    else if (filter.step && !filter.status) {
      query.steps = filter.step

      const statuses = STEP_STATUS_MAPPING[filter.step] || []
      const filteredStatuses = statuses.filter(status => status !== 'canceled' && status !== 'expired')

      if (filteredStatuses.length > 0) {
        query.status = filteredStatuses.sort().join(',')
      }
    }
    else {
      if (filter.step) query.steps = filter.step
      if (filter.status) query.status = filter.status
      if (filter.provider) query.provider = filter.provider
    }

    router.push({ path: '/projects', query })
  }

  function getBreakdownMobileLabel(label: string) {
    const mobileLabels: Record<string, string> = {
      [t('overview.projects_in_progress.breakdowns.approved_simulations')]: t('overview.projects_in_progress.mobile_labels.approved'),
      [t('overview.projects_in_progress.breakdowns.installations_in_progress')]: t('overview.projects_in_progress.mobile_labels.in_progress'),
      [t('overview.projects_in_progress.breakdowns.customer_data')]: t('overview.projects_in_progress.mobile_labels.data'),
      [t('overview.projects_in_progress.breakdowns.biometry_and_contract')]: t('overview.projects_in_progress.mobile_labels.biometry'),
      [t('overview.projects_in_progress.breakdowns.payment_model')]: t('overview.projects_in_progress.mobile_labels.model'),
    }

    return mobileLabels[label] || label
  }

  const isLoadingComputed = computed(() => {
    if (isLoading.value) return true
    if (isFetching.value) return true
    if (apiData.value && isProcessing.value && hasNullData.value) return true
    if (isError.value) return true
    return false
  })

  return {
    isLoading: isLoadingComputed,
    isEmpty,
    isRefreshing,
    displayData,
    progressBarWidth,
    handleRefresh,
    handleViewAll,
    handleTotalClick,
    handleCategoryClick,
    handleBreakdownClick,
    getBreakdownMobileLabel,
  }
}
